import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { api } from '../../App';
import { trackUpgradeEvent } from '../../utils/upgradeTracker';
import { Sparkles, MessageSquare, ChevronRight } from 'lucide-react';

/**
 * Dashboard widget: "X clients are waiting to hear from you".
 *
 * Renders ONLY for free-tier providers with ≥1 unread prospect message they
 * can't reply to. Pro-tier providers never see this, their API response is
 * always count=0, so the widget self-hides.
 *
 * Complements the weekly "missed opportunity" email by keeping the same
 * upgrade pressure visible every time the provider opens their dashboard.
 */
export function MissedProspectsWidget() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get('/providers/me/missed-prospects')
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Impression once we know there's at least one missed prospect. Firing
  // before this gate would inflate the denominator with providers who never
  // actually see the widget (count=0 hides it).
  useEffect(() => {
    if (data && data.count >= 1) {
      trackUpgradeEvent('impression', 'missed_prospects_widget');
    }
  }, [data]);

  if (loading || !data || data.count < 1) return null;

  const { count, estimated_value: estimatedValue, senders } = data;
  const word = count === 1 ? 'client is' : 'clients are';

  return (
    <div
      className="card-surface p-5 mb-6 border-l-4 border-l-[#D4AF37] bg-gradient-to-r from-[#FFF8E7] to-white"
      data-testid="missed-prospects-widget"
    >
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-11 h-11 rounded-full bg-[#D4AF37]/15 flex items-center justify-center">
          <MessageSquare className="h-5 w-5 text-[#D4AF37]" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-heading text-lg text-[#1A1A1A] mb-0.5">
            <span className="text-[#D4AF37] font-semibold">{count}</span>{' '}
            {word} waiting to hear from you
          </p>
          <p className="text-sm text-stone-600">
            {estimatedValue > 0 ? (
              <>Around <strong className="text-[#1A1A1A]">£{estimatedValue.toFixed(0)}</strong> in potential bookings sitting in your inbox.</>
            ) : (
              <>Prospect messages sitting in your inbox.</>
            )}
          </p>

          {senders && senders.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {senders.slice(0, 2).map((s) => (
                <li
                  key={s.user_id}
                  className="text-xs text-stone-500 flex items-start gap-2"
                  data-testid={`missed-prospect-preview-${s.user_id}`}
                >
                  <span className="font-medium text-[#1A1A1A]">{s.name}:</span>
                  <span className="truncate italic">&ldquo;{s.last_message}&rdquo;</span>
                </li>
              ))}
              {senders.length > 2 && (
                <li className="text-xs text-stone-400">+ {count - 2} more...</li>
              )}
            </ul>
          )}
        </div>

        <div className="shrink-0 flex flex-col gap-2">
          <Button
            onClick={() => {
              trackUpgradeEvent('click', 'missed_prospects_widget');
              navigate('/provider/dashboard?tab=earnings');
            }}
            className="btn-primary gap-1.5"
            data-testid="missed-prospects-upgrade-btn"
          >
            <Sparkles className="h-4 w-4" />
            Upgrade to Pro
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/messages')}
            className="text-xs gap-1 border-[#E6E4DD] text-stone-600"
            data-testid="missed-prospects-view-btn"
          >
            View messages
            <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}
